'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  Button,
  ConfirmationDialog,
  IconButton,
  LabelDefault,
  MetaDefault,
  SettingsCard,
  SettingsRow,
  Switch,
} from '@terra/ui-design-system';
import { Add01Icon, Delete01Icon } from '@terra/ui-icons';
import type {
  DiscoveryContentTypeChannel,
  DiscoveryContentTypeConfig,
} from '@terra/shared-asset-processing';
import {
  CONNECT_PROTO_CONTENT_TYPE,
  CONNECT_UNARY_PROTO_CONTENT_TYPE,
  GRPC_WEB_CONTENT_TYPE,
  GRPC_WEB_PROTO_CONTENT_TYPE,
  GRPC_WEB_TEXT_CONTENT_TYPE,
  getCrawlerMimeTypes,
  getTorchTrackingMimeTypes,
  isValidDiscoveryContentType,
  NO_MIME_TYPE,
  normalizeMimeKey,
} from '@terra/shared-asset-processing';
import { DiscoveryContentTypeAddDialog } from './DiscoveryContentTypeAddDialog';

type DangerousWarning =
  | {
      kind: 'html';
    }
  | {
      kind: 'empty';
      channel: DiscoveryContentTypeChannel;
    };
const DEFAULT_DESCRIPTION_MESSAGE_KEYS: Readonly<
  Record<string, `defaultDescriptions.${string}`>
> = {
  'application/json': 'defaultDescriptions.applicationJson',
  'application/xml': 'defaultDescriptions.applicationXml',
  'text/html': 'defaultDescriptions.textHtml',
  'text/xml': 'defaultDescriptions.textXml',
  'application/graphql': 'defaultDescriptions.applicationGraphql',
  'application/x-www-form-urlencoded':
    'defaultDescriptions.applicationXWwwFormUrlencoded',
  'text/event-stream': 'defaultDescriptions.textEventStream',
  'text/x-script': 'defaultDescriptions.textXScript',
  'text/csv': 'defaultDescriptions.textCsv',
  'application/octet-stream': 'defaultDescriptions.applicationOctetStream',
  [normalizeMimeKey(GRPC_WEB_CONTENT_TYPE)]:
    'defaultDescriptions.applicationGrpcWeb',
  [normalizeMimeKey(GRPC_WEB_PROTO_CONTENT_TYPE)]:
    'defaultDescriptions.applicationGrpcWebProto',
  [normalizeMimeKey(GRPC_WEB_TEXT_CONTENT_TYPE)]:
    'defaultDescriptions.applicationGrpcWebText',
  [normalizeMimeKey(CONNECT_PROTO_CONTENT_TYPE)]:
    'defaultDescriptions.applicationConnectProto',
  [normalizeMimeKey(CONNECT_UNARY_PROTO_CONTENT_TYPE)]:
    'defaultDescriptions.applicationProto',
  [normalizeMimeKey(NO_MIME_TYPE)]: 'defaultDescriptions.noMimeType',
};

type DiscoveryContentTypesSectionProps = {
  value: DiscoveryContentTypeConfig[];
  onChange: (configs: DiscoveryContentTypeConfig[]) => void;
  disabled?: boolean;
};
type PendingDangerousChange = {
  warning: DangerousWarning;
  nextValue: DiscoveryContentTypeConfig[];
};
const wouldEmptyChannel = ({
  configs,
  channel,
}: {
  configs: readonly DiscoveryContentTypeConfig[];
  channel: DiscoveryContentTypeChannel;
}): boolean =>
  (channel === 'crawler'
    ? getCrawlerMimeTypes(configs)
    : getTorchTrackingMimeTypes(configs)
  ).length === 0;
const isDisablingHtmlOnChannel = ({
  current,
  next,
  channel,
}: {
  current: readonly DiscoveryContentTypeConfig[];
  next: readonly DiscoveryContentTypeConfig[];
  channel: DiscoveryContentTypeChannel;
}): boolean => {
  const enabledKey =
    channel === 'crawler' ? 'crawlerEnabled' : 'torchTrackingEnabled';
  const currentHtml = current.find(
    (config) => normalizeMimeKey(config.mimeType) === 'text/html',
  );
  const nextHtml = next.find(
    (config) => normalizeMimeKey(config.mimeType) === 'text/html',
  );

  return Boolean(currentHtml?.[enabledKey] && !nextHtml?.[enabledKey]);
};

export function DiscoveryContentTypesSection({
  value,
  onChange,
  disabled,
}: DiscoveryContentTypesSectionProps) {
  const t = useTranslations('settings.discoveryContentTypes');
  const tCommon = useTranslations('common');
  const tSessionContentTypes = useTranslations(
    'proxy.tracking.sessionContentTypes',
  );
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newMimeType, setNewMimeType] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [pendingDangerousChange, setPendingDangerousChange] =
    useState<PendingDangerousChange | null>(null);
  const allCrawlerEnabled =
    value.length > 0 && value.every((config) => config.crawlerEnabled);
  const allTorchTrackingEnabled =
    value.length > 0 && value.every((config) => config.torchTrackingEnabled);
  const displayMimeLabel = (mimeType: string): string =>
    mimeType === NO_MIME_TYPE ? tSessionContentTypes('noMimeType') : mimeType;
  const getRowDescription = (
    config: DiscoveryContentTypeConfig,
  ): string | undefined => {
    if (config.origin === 'DEFAULT') {
      const messageKey =
        DEFAULT_DESCRIPTION_MESSAGE_KEYS[normalizeMimeKey(config.mimeType)];

      return messageKey ? t(messageKey) : undefined;
    }

    return config.description ?? undefined;
  };

  const getDangerousChangeTitle = (warning: DangerousWarning): string => {
    if (warning.kind === 'html') {
      return t('htmlDisableTitle');
    }

    return warning.channel === 'crawler'
      ? t('emptyCrawlerTitle')
      : t('emptyTorchTrackingTitle');
  };

  const requestChange = (nextValue: DiscoveryContentTypeConfig[]) => {
    const emptiesCrawler =
      wouldEmptyChannel({ configs: nextValue, channel: 'crawler' }) &&
      !wouldEmptyChannel({ configs: value, channel: 'crawler' });
    const emptiesTorchTracking =
      wouldEmptyChannel({ configs: nextValue, channel: 'torchTracking' }) &&
      !wouldEmptyChannel({ configs: value, channel: 'torchTracking' });

    if (emptiesCrawler || emptiesTorchTracking) {
      setPendingDangerousChange({
        warning: {
          kind: 'empty',
          channel: emptiesCrawler ? 'crawler' : 'torchTracking',
        },
        nextValue,
      });

      return;
    }

    if (
      isDisablingHtmlOnChannel({
        current: value,
        next: nextValue,
        channel: 'crawler',
      }) ||
      isDisablingHtmlOnChannel({
        current: value,
        next: nextValue,
        channel: 'torchTracking',
      })
    ) {
      setPendingDangerousChange({
        warning: { kind: 'html' },
        nextValue,
      });

      return;
    }

    onChange(nextValue);
  };

  const updateConfig = (
    mimeType: string,
    patch: Partial<
      Pick<
        DiscoveryContentTypeConfig,
        'crawlerEnabled' | 'torchTrackingEnabled'
      >
    >,
  ) => {
    const key = normalizeMimeKey(mimeType);

    requestChange(
      value.map((config) =>
        normalizeMimeKey(config.mimeType) === key
          ? { ...config, ...patch }
          : config,
      ),
    );
  };

  const setChannelForAll = (
    channel: DiscoveryContentTypeChannel,
    enabled: boolean,
  ) => {
    const key =
      channel === 'crawler' ? 'crawlerEnabled' : 'torchTrackingEnabled';

    requestChange(value.map((config) => ({ ...config, [key]: enabled })));
  };

  const removeCustom = (mimeType: string) => {
    const key = normalizeMimeKey(mimeType);

    requestChange(
      value.filter(
        (config) =>
          !(
            config.origin === 'CUSTOM' &&
            normalizeMimeKey(config.mimeType) === key
          ),
      ),
    );
  };

  const handleOpenAddDialog = (open: boolean) => {
    setIsAddDialogOpen(open);
    if (!open) {
      setNewMimeType('');
      setNewDescription('');
      setAddError(null);
    }
  };

  const handleAddType = () => {
    const trimmed = newMimeType.trim();

    if (!isValidDiscoveryContentType(trimmed)) {
      setAddError(t('invalidMimeType'));

      return;
    }

    const key = normalizeMimeKey(trimmed);

    if (value.some((config) => normalizeMimeKey(config.mimeType) === key)) {
      setAddError(t('alreadyInList'));

      return;
    }

    const trimmedDescription = newDescription.trim();

    onChange([
      ...value,
      {
        mimeType: trimmed,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        origin: 'CUSTOM',
        crawlerEnabled: true,
        torchTrackingEnabled: true,
      },
    ]);
    handleOpenAddDialog(false);
  };

  const handleConfirmDangerousChange = () => {
    if (!pendingDangerousChange) {
      return;
    }

    onChange(pendingDangerousChange.nextValue);
    setPendingDangerousChange(null);
  };

  const renderRow = (config: DiscoveryContentTypeConfig) => {
    const description = getRowDescription(config);
    const typeLabel =
      config.origin === 'CUSTOM' ? tCommon('custom') : tCommon('default');

    return (
      <HStack key={config.mimeType} gap="3" alignItems="center" w="full">
        <VStack gap="0" alignItems="flex-start" flex="1" minW="0">
          <BodyDefault truncate>
            {displayMimeLabel(config.mimeType)}
          </BodyDefault>
          {description ? (
            <MetaDefault color="fg.muted" truncate>
              {description}
            </MetaDefault>
          ) : null}
        </VStack>
        <Box w="16" display="flex" justifyContent="center" flexShrink={0}>
          <MetaDefault color="fg.muted">{typeLabel}</MetaDefault>
        </Box>
        <Box w="24" display="flex" justifyContent="center" flexShrink={0}>
          <Switch
            size="sm"
            checked={config.crawlerEnabled}
            disabled={disabled}
            onCheckedChange={(details) =>
              updateConfig(config.mimeType, {
                crawlerEnabled: details.checked,
              })
            }
          />
        </Box>
        <Box w="28" display="flex" justifyContent="center" flexShrink={0}>
          <Switch
            size="sm"
            checked={config.torchTrackingEnabled}
            disabled={disabled}
            onCheckedChange={(details) =>
              updateConfig(config.mimeType, {
                torchTrackingEnabled: details.checked,
              })
            }
          />
        </Box>
        <Box w="8" display="flex" justifyContent="center" flexShrink={0}>
          {config.origin === 'CUSTOM' ? (
            <IconButton
              size="xs"
              variant="ghost"
              aria-label={tCommon('remove')}
              disabled={disabled}
              onClick={() => removeCustom(config.mimeType)}
            >
              <Delete01Icon size={14} />
            </IconButton>
          ) : null}
        </Box>
      </HStack>
    );
  };

  return (
    <>
      <SettingsCard>
        <SettingsRow
          label={t('title')}
          tooltip={t('tooltip')}
          control={
            <Button
              size="xs"
              variant="outline"
              onClick={() => handleOpenAddDialog(true)}
              disabled={disabled}
            >
              <Add01Icon size={14} />
              {t('addType')}
            </Button>
          }
        />
        <HStack gap="3" alignItems="flex-start" w="full">
          <LabelDefault color="fg.neutral.muted" flex="1" minW="0">
            {t('contentType')}
          </LabelDefault>
          <LabelDefault
            color="fg.neutral.muted"
            w="16"
            flexShrink={0}
            textAlign="center"
          >
            {tCommon('origin')}
          </LabelDefault>
          <VStack gap="1" alignItems="center" w="24" flexShrink={0}>
            <LabelDefault color="fg.neutral.muted">{t('crawler')}</LabelDefault>
            <Switch
              size="sm"
              checked={allCrawlerEnabled}
              disabled={disabled || value.length === 0}
              onCheckedChange={(details) =>
                setChannelForAll('crawler', details.checked)
              }
            />
          </VStack>
          <VStack gap="1" alignItems="center" w="28" flexShrink={0}>
            <LabelDefault color="fg.neutral.muted" textAlign="center">
              {t('torchTracking')}
            </LabelDefault>
            <Switch
              size="sm"
              checked={allTorchTrackingEnabled}
              disabled={disabled || value.length === 0}
              onCheckedChange={(details) =>
                setChannelForAll('torchTracking', details.checked)
              }
            />
          </VStack>
          <Box w="8" flexShrink={0} />
        </HStack>
        {value.map(renderRow)}
      </SettingsCard>

      <DiscoveryContentTypeAddDialog
        open={isAddDialogOpen}
        onOpenChange={handleOpenAddDialog}
        mimeType={newMimeType}
        description={newDescription}
        error={addError}
        disabled={disabled}
        onMimeTypeChange={(next) => {
          setNewMimeType(next);
          setAddError(null);
        }}
        onDescriptionChange={setNewDescription}
        onSubmit={handleAddType}
      />

      <ConfirmationDialog
        isOpen={Boolean(pendingDangerousChange)}
        onOpenChange={({ open }) => {
          if (!open) {
            setPendingDangerousChange(null);
          }
        }}
        onConfirm={handleConfirmDangerousChange}
        variant="warning"
        title={
          pendingDangerousChange
            ? getDangerousChangeTitle(pendingDangerousChange.warning)
            : ''
        }
        descriptionContent={
          pendingDangerousChange?.warning.kind === 'html' ? (
            <>
              {t('htmlImpactLead')}
              <br />
              {t('htmlImpactFollowUp')}
            </>
          ) : (
            t('emptyTypesWarning')
          )
        }
        confirmText={tCommon('confirm')}
        cancelText={tCommon('cancel')}
      />
    </>
  );
}
